/**
 * File: Blog.jsx
 * Path: /src/pages/Blog.jsx
 * Description: Blog listing page for Veritect.ai showcasing thought leadership content
 * Key Features: Article cards, filtering options, and responsive grid layout
 */

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import ModernCtaSection from '../components/common/ModernCtaSection';
import useIntersectionObserver from '../hooks/useIntersectionObserver';

// Blog post card component
const BlogPostCard = ({ post, index }) => {
  const [ref, isVisible] = useIntersectionObserver({
    threshold: 0.1,
    triggerOnce: true,
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.7, delay: index * 0.1 }}
    >
      <Card 
        interactive 
        className="h-full flex flex-col transition-all duration-500"
      >
        {post.image && (
          <div className="w-full h-48 mb-5 overflow-hidden rounded-t-xl relative">
            <img 
              src={post.image} 
              alt={post.title} 
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
            {post.featured && (
              <div className="absolute top-4 left-4 bg-accent-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                Featured
              </div>
            )}
          </div>
        )}
        
        <div className="flex-1 flex flex-col p-6 pt-0">
          <div className="flex items-center text-dark-400 text-sm mb-3">
            <span>{post.date}</span>
            <span className="mx-2">•</span>
            <span>{post.category}</span>
          </div>
          
          <h3 className="text-xl font-bold mb-3">{post.title}</h3>
          <p className="text-dark-500 mb-4 flex-grow">{post.excerpt}</p>
          
          <div className="flex items-center justify-between mt-auto pt-4 border-t border-dark-100">
            <div className="flex items-center">
              <div className="w-8 h-8 rounded-full overflow-hidden mr-3">
                <img 
                  src={post.author.avatar || "/placeholder-avatar.jpg"} 
                  alt={post.author.name} 
                  className="w-full h-full object-cover"
                />
              </div>
              <span className="text-dark-700 text-sm font-medium">{post.author.name}</span>
            </div>
            
            <Link 
              to={`/blog/${post.slug}`}
              className="text-primary-600 text-sm font-medium hover:text-primary-700 transition-colors"
            >
              Read More →
            </Link>
          </div>
        </div>
      </Card>
    </motion.div>
  );
};

// Category filter button component
const CategoryFilter = ({ name, active, onClick }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
      active 
        ? 'bg-primary-600 text-white shadow-md' 
        : 'bg-white text-dark-600 hover:bg-primary-50'
    }`}
  >
    {name}
  </button>
);

const Blog = () => {
  const [sectionRef, isVisible] = useIntersectionObserver({
    threshold: 0.1,
    triggerOnce: true
  });
  
  const [activeFilter, setActiveFilter] = useState('All');
  
  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Blog posts data
  const blogPosts = [
    {
      id: 1,
      title: "The Weight of Justice: AI Solutions for India's Judicial Caseload Crisis",
      slug: "weight-of-justice-ai-solutions-india-judicial-caseload",
      excerpt: "A comparative analysis of judicial caseloads between India and other common law jurisdictions, highlighting the potential for AI to address India's unprecedented case backlog.",
      date: "May 5, 2025",
      category: "Legal Research",
      image: "/placeholder-blog-1.jpg",
      featured: true,
      author: {
        name: "Adv Vibhor Gupta",
        avatar: "/placeholder-avatar.jpg",
        title: "Managing Partner, AMV LAW"
      }
    },
    {
      id: 2,
      title: "Predictive Analytics: The Future of Case Outcome Forecasting",
      slug: "predictive-analytics-future-case-outcome-forecasting",
      excerpt: "How machine learning models are revolutionizing litigation strategy by forecasting judicial decisions with increasing accuracy.",
      date: "April 28, 2025",
      category: "Judgment Prediction",
      image: "/placeholder-blog-2.jpg",
      featured: false,
      author: {
        name: "Dr. Sarah Chen",
        avatar: "/placeholder-avatar.jpg",
        title: "AI Research Lead"
      }
    },
    {
      id: 3,
      title: "Beyond Document Review: AI for Complex Contract Analysis",
      slug: "beyond-document-review-ai-complex-contract-analysis",
      excerpt: "An exploration of how advanced NLP techniques are transforming contract review from simple term extraction to comprehensive risk assessment.",
      date: "April 15, 2025",
      category: "Contract Analysis",
      image: "/placeholder-blog-3.jpg",
      featured: false,
      author: {
        name: "Michael Reynolds",
        avatar: "/placeholder-avatar.jpg",
        title: "Product Director"
      }
    },
    {
      id: 4,
      title: "Digital Transformation in Legal Practice: 2025 Trends",
      slug: "digital-transformation-legal-practice-2025-trends",
      excerpt: "The key technology trends reshaping law firms and corporate legal departments in the coming year.",
      date: "April 3, 2025",
      category: "Legal Tech",
      image: "/placeholder-blog-4.jpg",
      featured: false,
      author: {
        name: "Emma Thompson",
        avatar: "/placeholder-avatar.jpg",
        title: "Legal Innovation Consultant"
      }
    },
    {
      id: 5,
      title: "Building Ethical Guardrails for Legal AI Systems",
      slug: "building-ethical-guardrails-legal-ai-systems",
      excerpt: "Essential considerations for developing AI legal tools that maintain judicial independence and fairness.",
      date: "March 22, 2025",
      category: "Ethics",
      image: "/placeholder-blog-5.jpg",
      featured: false,
      author: {
        name: "Prof. James Liu",
        avatar: "/placeholder-avatar.jpg",
        title: "AI Ethics Researcher"
      }
    },
    {
      id: 6,
      title: "Case Study: How Veritect AI Reduced Discovery Time by 73%",
      slug: "case-study-veritect-ai-reduced-discovery-time",
      excerpt: "A detailed look at how a major law firm transformed their document review process using our AI platform.",
      date: "March 10, 2025",
      category: "Case Studies",
      image: "/placeholder-blog-6.jpg",
      featured: false,
      author: {
        name: "Rachel Martinez",
        avatar: "/placeholder-avatar.jpg",
        title: "Customer Success Lead"
      }
    }
  ];
  
  // Extract unique categories
  const categories = ['All', ...new Set(blogPosts.map(post => post.category))];
  
  // Filter posts based on active category
  const filteredPosts = activeFilter === 'All' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === activeFilter);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 -z-10 w-1/3 h-1/3 bg-primary-100 rounded-bl-full opacity-70"></div>
        <div className="absolute bottom-0 left-0 -z-10 w-1/4 h-1/4 bg-secondary-100 rounded-tr-full opacity-70"></div>
        
        {/* Floating decorative elements */}
        <motion.div 
          className="absolute right-1/4 top-1/5 hidden md:block"
          animate={{ 
            y: [0, -15, 0], 
            opacity: [0.4, 0.7, 0.4] 
          }}
          transition={{ 
            duration: 5, 
            repeat: Infinity, 
            repeatType: "reverse" 
          }}
        >
          <div className="w-16 h-16 border-2 border-primary-500/30 rounded-lg rotate-12"></div>
        </motion.div>
        
        <motion.div 
          className="absolute left-1/4 bottom-1/4 hidden md:block"
          animate={{ 
            y: [0, 15, 0], 
            opacity: [0.3, 0.6, 0.3] 
          }}
          transition={{ 
            duration: 7, 
            repeat: Infinity, 
            repeatType: "reverse", 
            delay: 1 
          }}
        >
          <div className="w-12 h-12 border-2 border-secondary-500/30 rounded-full"></div>
        </motion.div>
        
        <div className="container-padding mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            <motion.span 
              className="inline-block px-4 py-2 bg-primary-50 text-primary-600 rounded-full text-sm font-medium mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Insights & Thought Leadership
            </motion.span>
            
            <motion.h1 
              className="text-4xl md:text-5xl font-bold mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              The Veritect <span className="gradient-text">Blog</span>
            </motion.h1>
            
            <motion.p 
              className="text-xl text-dark-600 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Exploring the frontiers of legal AI, sharing industry insights, and showcasing 
              how technology is transforming the practice of law.
            </motion.p>
          </div>
        </div>
      </section>
      
      {/* Blog Content Section */}
      <section className="py-20 bg-dark-50" ref={sectionRef}>
        <div className="container-padding mx-auto">
          {/* Category filters */}
          <div className="mb-12 overflow-x-auto">
            <div className="flex space-x-3 pb-4">
              {categories.map((category) => (
                <CategoryFilter
                  key={category}
                  name={category}
                  active={activeFilter === category}
                  onClick={() => setActiveFilter(category)}
                />
              ))}
            </div>
          </div>
          
          {/* Featured post (if available and filter allows) */}
          {activeFilter === 'All' || activeFilter === 'Legal Research' ? (
            <motion.div 
              className="mb-16"
              initial={{ opacity: 0, y: 30 }}
              animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.7 }}
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 bg-white p-6 rounded-xl shadow-lg">
                <div className="overflow-hidden rounded-xl">
                  <img 
                    src="/placeholder-blog-1.jpg" 
                    alt="The Weight of Justice" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex flex-col justify-center">
                  <div className="flex items-center text-dark-400 text-sm mb-3">
                    <span>May 5, 2025</span>
                    <span className="mx-2">•</span>
                    <span>Legal Research</span>
                    <span className="ml-3 bg-accent-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                      Featured
                    </span>
                  </div>
                  
                  <h2 className="text-2xl md:text-3xl font-bold mb-4">
                    The Weight of Justice: AI Solutions for India's Judicial Caseload Crisis
                  </h2>
                  
                  <p className="text-dark-500 mb-6">
                    A comparative analysis of judicial caseloads between India and other common law jurisdictions,
                    highlighting the stark disparities in pending cases and judge-to-population ratios.
                    This article examines India's unique structural factors contributing to this burden
                    and analyzes various AI-powered solutions for the judicial system.
                  </p>
                  
                  <div className="flex items-center mb-6">
                    <div className="w-10 h-10 rounded-full overflow-hidden mr-3">
                      <img 
                        src="/placeholder-avatar.jpg" 
                        alt="Adv Vibhor Gupta" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <div className="text-dark-700 font-medium">Adv Vibhor Gupta</div>
                      <div className="text-dark-500 text-sm">Managing Partner, AMV LAW</div>
                    </div>
                  </div>
                  
                  <Button 
                    variant="primary"
                    to="/blog/weight-of-justice-ai-solutions-india-judicial-caseload"
                  >
                    Read Full Article
                  </Button>
                </div>
              </div>
            </motion.div>
          ) : null}
          
          {/* Blog post grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post, index) => 
              // Skip the featured post in the regular grid if it's already displayed
              (post.id === 1 && (activeFilter === 'All' || activeFilter === 'Legal Research')) ? null : (
                <BlogPostCard key={post.id} post={post} index={index} />
              )
            )}
          </div>
          
          {/* Empty state */}
          {filteredPosts.length === 0 && (
            <div className="text-center py-20">
              <div className="text-4xl mb-4">🔍</div>
              <h3 className="text-xl font-bold mb-2">No posts found</h3>
              <p className="text-dark-500 mb-6">
                We couldn't find any blog posts in this category. Please try another category or check back later.
              </p>
              <Button 
                variant="outline"
                onClick={() => setActiveFilter('All')}
              >
                View All Posts
              </Button>
            </div>
          )}
        </div>
      </section>
      
      {/* CTA Section */}
      <ModernCtaSection
        title="Want to Stay Updated?"
        description="Subscribe to our newsletter to receive the latest insights on legal AI, product updates, and industry trends."
      >
        <div className="max-w-md mx-auto">
          <div className="flex flex-col sm:flex-row gap-3">
            <input 
              type="email" 
              placeholder="Your email address" 
              className="flex-grow px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/30"
            />
            <Button 
              variant="primary" 
              className="whitespace-nowrap"
            >
              Subscribe
            </Button>
          </div>
          <div className="text-sm text-primary-100 mt-3">
            We respect your privacy. Unsubscribe at any time.
          </div>
        </div>
      </ModernCtaSection>
    </motion.div>
  );
};

export default Blog;